import OneSignal from "react-onesignal";
import { PUBLIC_PTC_ONESIGNAL_APP_ID, PUBLIC_PTC_ONESIGNAL_LOG_LEVEL } from "../../env";
import { app } from "../../init";
import { confirm } from "../alert-dialog";

const allowedLogLevels = ["debug", "info", "warn", "error", "trace"] as const;
export const push = {
    debug: (message: string, ...args: unknown[]) => {
        const logLevel = PUBLIC_PTC_ONESIGNAL_LOG_LEVEL as (typeof allowedLogLevels)[number] | undefined;

        if (logLevel && allowedLogLevels.includes(logLevel)) {
            console[logLevel]("%c🛎️ " + message, "background: white; color: red", ...args);
        }
    },
    init: async (): Promise<boolean> => {
        const appId = PUBLIC_PTC_ONESIGNAL_APP_ID;
        const logLevel = PUBLIC_PTC_ONESIGNAL_LOG_LEVEL as (typeof allowedLogLevels)[number] | undefined;
        if (!appId || appId === "") return false;

        try {
            await OneSignal.init({ appId });
            if (logLevel) {
                OneSignal.Debug.setLogLevel(logLevel);
            }
            push.debug("Init done for appId:", appId);

            // ask for permission if no choice is stored -
            // default case when app is opened for the first time
            if (app.state.oneSignalConsentGiven === null) {
                await push.askForPushPermission();
            }

            if (app.state.oneSignalConsentGiven) {
                await push.login();
            }
            return true;
        } catch (err) {
            push.debug("OneSignal init failed:", err);
            return false;
        }
    },
    login: async () => {
        push.debug("Logging in accountId:", app.profile?.accountId);

        await OneSignal.login(app.profile?.accountId?.toString() || "")
            .then(() => {
                push.debug("OneSignal login done", OneSignal.User);
            })
            .catch((err) => {
                push.debug("OneSignal login failed:", err);
            });
        await app.update({
            oneSignalConsentGiven: true,
            oneSignalConsentTimestamp: new Date(),
        });
    },
    logout: async () => {
        await OneSignal.logout()
            .then(() => {
                push.debug("OneSignal logout done", OneSignal.User);
            })
            .catch((err) => {
                push.debug("OneSignal logout failed:", err);
            });
        await app.update({
            oneSignalConsentGiven: false,
            oneSignalConsentTimestamp: null,
        });
    },
    setConsent: async (consentGiven: boolean) => {
        await OneSignal.setConsentGiven(consentGiven)
            .then(() => {
                push.debug("OneSignal set consent given done", consentGiven);
            })
            .catch((err) => {
                push.debug("OneSignal set consent given failed:", err);
            });

        if (consentGiven) {
            await OneSignal.User.PushSubscription.optIn()
                .then(() => {
                    push.debug("OneSignal opt in done", OneSignal.User);
                })
                .catch((err) => {
                    push.debug("OneSignal opt in failed:", err);
                });
        } else {
            await OneSignal.User.PushSubscription.optOut()
                .then(() => {
                    push.debug("OneSignal opt out done", OneSignal.User);
                })
                .catch((err) => {
                    push.debug("OneSignal opt out failed:", err);
                });
        }

        await app.update({
            oneSignalConsentGiven: consentGiven,
            oneSignalConsentTimestamp: consentGiven ? new Date() : null,
        });
    },
    setSubscription: async (subcribe: boolean) => {
        if (subcribe) {
            await OneSignal.User.PushSubscription.optIn()
                .then(() => {
                    push.debug("OneSignal opt in done", OneSignal.User);
                })
                .catch((err) => {
                    push.debug("OneSignal opt in failed:", err);
                });
        } else {
            await OneSignal.User.PushSubscription.optOut()
                .then(() => {
                    push.debug("OneSignal opt out done", OneSignal.User);
                })
                .catch((err) => {
                    push.debug("OneSignal opt out failed:", err);
                });
        }
    },
    get isSubscribed() {
        return OneSignal.User.PushSubscription.optedIn;
    },

    askForPushPermission: async () => {
        const consentGiven = await confirm(
            "Wollen Sie aktuelle Meldungen über ihr Unternehmen per Push-Nachricht erhalten?",
            "Erlauben",
            "Ablehnen"
        );
        await push.setConsent(consentGiven);
    },
};
